import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  CircularProgress,
  Typography,
} from "@mui/material";
import useAdd from "../../api/useAdd";
import useFetchObjects from "../../api/useFetchObjects";
import { useTranslation } from "react-i18next";

const CollectPaymentDialog = ({
  open,
  onClose,
  customer,
  token,
  onSuccess,
}) => {
  const { t } = useTranslation();

  const {
    handleAdd: handleBalancePaymentAdd,
    loading: balancePaymentLoading,
    resetForm: balancePaymentReset,
  } = useAdd("shop/customer-balance-payments", token);

  const { data: currenciesData, isLoading: currencyLoading } = useFetchObjects(
    ["currency"],
    "shop/currencies",
    token
  );

  const currencies = currenciesData?.results ?? currenciesData ?? [];

  const [selectedCurrency, setSelectedCurrency] = useState(null);
  const [amount, setAmount] = useState("");
  const [paymentDate, setPaymentDate] = useState(
    new Date().toISOString().slice(0, 16)
  );
  const [notes, setNotes] = useState("");

  const resetForm = () => {
    setSelectedCurrency(null);
    setAmount("");
    setPaymentDate(new Date().toISOString().slice(0, 16));
    setNotes("");
  };

  useEffect(() => {
    if (!open) resetForm();
  }, [open]);

  useEffect(() => {
    if (balancePaymentReset) {
      resetForm();
      onSuccess?.();
      onClose();
    }
  }, [balancePaymentReset, onClose, onSuccess]);

  const handleSubmit = () => {
    if (!selectedCurrency || !amount || !customer?.id) return;
    handleBalancePaymentAdd({
      customer: customer.id,
      currency: selectedCurrency.id,
      amount: Number(amount),
      payment_date: paymentDate,
      notes: notes.trim() || undefined,
    });
  };

  const currentBalance = customer?.total_owed_by_currency;
  const balanceStr =
    currentBalance && typeof currentBalance === "object"
      ? Object.entries(currentBalance)
          .map(([cur, amt]) => `${Number(amt).toFixed(2)} ${cur}`)
          .join(", ") || "0"
      : "0";

  if (!customer) return null;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {t("customer.collectPayment", "Collect payment")} – {customer.name}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Box sx={{ p: 2, bgcolor: "grey.100", borderRadius: 1 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              {t("salePayment.currentBalance", "Current balance")}
            </Typography>
            <Typography variant="body1" fontWeight="bold">
              {balanceStr}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              {t("customer.payAgainstBalanceHelp", "Recording a payment will reduce this balance.")}
            </Typography>
          </Box>

          <TextField
            select
            SelectProps={{ native: true }}
            margin="dense"
            label={t("salePayment.selectCurrency", "Currency")}
            fullWidth
            required
            value={selectedCurrency?.id ?? ""}
            onChange={(e) => {
              const c = currencies.find((x) => String(x.id) === e.target.value);
              setSelectedCurrency(c || null);
            }}
            disabled={currencyLoading}
          >
            <option value="" />
            {(currencies || []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.title}
              </option>
            ))}
          </TextField>

          <TextField
            margin="dense"
            label={t("salePayment.amount", "Amount")}
            type="number"
            fullWidth
            required
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            inputProps={{ min: 0, step: "0.01" }}
          />
          <TextField
            margin="dense"
            label={t("salePayment.paymentDate", "Payment date")}
            type="datetime-local"
            fullWidth
            required
            value={paymentDate}
            onChange={(e) => setPaymentDate(e.target.value)}
          />
          <TextField
            margin="dense"
            label={t("salePayment.notes", "Notes")}
            multiline
            rows={2}
            fullWidth
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" disabled={balancePaymentLoading}>
          {t("customer.cancel", "Cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={balancePaymentLoading || !selectedCurrency || !amount}
          sx={{ minWidth: 100 }}
        >
          {balancePaymentLoading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            t("customer.recordPayment", "Record payment")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CollectPaymentDialog;
